import { Badge, Menu } from "antd";
import { THEME_TYPE_LITE } from "constants/ThemeSetting";
import { useTestStatusCount } from "data/queries/Misc";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  API_URL,
  ClientSelectedProgram,
  // FacilityTypeEnums,
  UserRoleEnums,
} from "utils/Constant";
import CustomScrollbars from "utils/CustomScrollbars";
import { programEnum } from "utils/Helpers";
import { getCookieValue } from "utils/Storage";
import { useEspnDashboardCounts } from "data/queries/Espn";

import {
  AdminMenuSections,
  EspnMenuSection,
  GeneralProgramMenuSection,
  LocationAnalyticsMenuSection,
  PublicProgramsMenuSection,
  UniversalMenuSections,
} from "./sections";
import espnAdminItems from "./sections/espnMenuConfig";
import espnFieldItems from "./sections/espnFieldConfig";
import SidebarLogo from "./SidebarLogo";

// eslint-disable-next-line react-refresh/only-export-components
const SidebarContent = ({ sidebarCollapsed, setSidebarCollapsed }) => {
  const { navStyle, themeType } = useSelector(({ settings }) => settings);
  const { authUser } = useSelector(({ auth }) => auth);
  const location = useLocation();

  const enlis_program = getCookieValue(ClientSelectedProgram);

  const [menuSelectedKey, setMenuSelectedKey] = useState("");
  const [openKeys, setOpenKeys] = useState([]);

  const {
    SUPER_ADMIN,
    ADMIN,
    DATA_ANALYST,
    SUPERVISOR,
    ANALYST,
    RECEPTIONIST,
  } = UserRoleEnums;

  const public_programs = authUser.programs_roles
    ?.filter(
      (item) =>
        ![
          programEnum.TB,
          programEnum.EID,
          programEnum.VIRAL_LOAD,
          programEnum.GENERAL,
          programEnum.ESPN,
        ].includes(item?.programs),
    )
    ?.map((item) => item?.programs);

  const rootSubmenuKeys = [
    "administrations",
    "facility-administrations",
    "commodities",
    "commodities_consumables",
    "dashboards/facility",
    "facility-report",
  ];

  const getRootSubmenuKey = (pathKey) => {
    if (!pathKey) return "";
    if (pathKey.startsWith("administrations/facility")) {
      return "facility-administrations";
    }
    if (pathKey.startsWith("administrations/global")) {
      return "administrations";
    }
    if (
      pathKey.startsWith("commodities/consumable") ||
      pathKey.startsWith("commodities/createConsumable")
    ) {
      return "commodities_consumables";
    }
    if (pathKey.startsWith("commodities/")) {
      return "commodities";
    }
    if (pathKey.startsWith("dashboards/facility")) {
      return "dashboards/facility";
    }
    if (pathKey.startsWith("reports/ph-report")) {
      return "facility-report";
    }
    return pathKey.split("/")[0] ?? "";
  };

  useEffect(() => {
    const currentPageRoute = location.pathname.replace(/^\/+/, "");
    setMenuSelectedKey(currentPageRoute);
    const nextOpenKey = getRootSubmenuKey(currentPageRoute);
    setOpenKeys(nextOpenKey ? [nextOpenKey] : []);
  }, [location.pathname]);

  const roles = useMemo(() => {
    const _roles = authUser.programs_roles?.map((item) => item?.roles);
    return Array.from(new Set(_roles)) || [];
  }, [authUser.programs_roles]);

  const espnRole = useMemo(
  () =>
    authUser.programs_roles?.find(
      (item) => item?.programs === programEnum.ESPN,
    )?.roles,
  [authUser.programs_roles],
);

  const role = useMemo(() => {
    return authUser.programs_roles?.find(
      (item) => item?.programs === enlis_program,
    )?.roles;
  }, [authUser.programs_roles, enlis_program]);

  const canSeeFacilityAnalytics = useMemo(() => {
    if (
      role === SUPERVISOR ||
      role === DATA_ANALYST ||
      role === RECEPTIONIST ||
      role === ANALYST
    )
      return true;
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const isGlobalAdmin = useMemo(
    () => roles.includes(SUPER_ADMIN),
    [SUPER_ADMIN, roles],
  );
  const isFacilityAdmin = useMemo(() => roles.includes(ADMIN), [ADMIN, roles]);

  const flatPublicPrograms = public_programs?.filter((item) => item);

  const { currentProgram, currentState, currentLga } = useSelector(
    ({ common }) => common,
  );

  const isPublicProgram = useMemo(() => {
    return (
      [
        programEnum.TB,
        programEnum.EID,
        programEnum.VIRAL_LOAD,
        ...(flatPublicPrograms || []),
      ].includes(currentProgram) && !isFacilityAdmin
    );
  }, [currentProgram, flatPublicPrograms, isFacilityAdmin]);

  const statusParam = useMemo(() => {
    if (!currentProgram) return null;
    if (currentProgram === programEnum.GENERAL)
      return API_URL.TEST_STATUS_COUNT_GENERAL;
    if (flatPublicPrograms?.includes(currentProgram)) {
      return `${API_URL.TEST_STATUS_COUNT_PH}/?program__name=${currentProgram}`;
    }
    return null;
  }, [currentProgram, flatPublicPrograms]);

  const { data: testCounts = [], isLoading: countsLoading } =
    useTestStatusCount({
      param: statusParam,
      options: { enabled: !!currentProgram, retry: 0 },
    });

  // const getCountByDisplay = (display) =>
  //   Number(
  //     testCounts?.find((i) => i?.test_order_status === display)?.count ?? 0
  //   );
  const getCountByDisplay = (display) => {
    if (!testCounts) return 0;
    const list = Array.isArray(testCounts)
      ? testCounts
      : Array.isArray(testCounts?.results)
        ? testCounts.results
        : typeof testCounts === "object"
          ? [testCounts]
          : [];

    const item = list.find((i) => i?.test_order_status === display);
    return Number(item?.count ?? 0);
  };

  const countUi = (status = null) => {
    if (!status || countsLoading) return null;
    const list = Array.isArray(status) ? status : [status];
    const value = list.reduce((acc, s) => acc + getCountByDisplay(s), 0);

    return (
      <>
        {value > 0 ? (
          <Badge
            showZero={false}
            status={"processing"}
            offset={[3, -7]}
            size="small"
            count={value}
            style={{
              fontSize: 11,
            }}
          />
        ) : null}
      </>
    );
  };

  const { data: espnDashboardCounts, isLoading: espnCountsLoading } =
    useEspnDashboardCounts({
      enabled: currentProgram === programEnum.ESPN,
    });

  const espnCounts = useMemo(() => {
    if (!espnDashboardCounts) return {};

    const sumByStatus = (arr, field, statusValue) =>
      arr?.find((item) => item[field] === statusValue)?.count || 0;

    return {
      // Field items (base keys)
      "moore-swab-deployment": espnDashboardCounts.moore_swab_deployment || 0,
      "sample-collection": espnDashboardCounts.collection || 0,
      "incidence-reporting": espnDashboardCounts.incidence || 0,
      "sample-submission": espnDashboardCounts.submission || 0,
      "sample-collection-supervision": espnDashboardCounts.supervision || 0,

      // Admin/workflow items (base keys)
      "sample-reception": espnDashboardCounts.reception || 0,
      "sample-preprocessing": espnDashboardCounts.preprocessing || 0,
      "sample-extraction": espnDashboardCounts.extraction || 0,
      "laboratory-results": sumByStatus(
        espnDashboardCounts.lab_results,
        "result_status",
        "entered",
      ),
      "laboratory-validation": sumByStatus(
        espnDashboardCounts.lab_validation,
        "validation_status",
        "entered",
      ),
      "laboratory-approval": sumByStatus(
        espnDashboardCounts.lab_approval,
        "approval_status",
        "validated",
      ),
    };
  }, [espnDashboardCounts]);
  return (
    <>
      <SidebarLogo
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
      />
      <div className="gx-sidebar-content">
        <CustomScrollbars className="gx-layout-sider-scrollbar2">
          <Menu
            openKeys={openKeys}
            theme={themeType === THEME_TYPE_LITE ? "lite" : "dark"}
            mode="inline"
            selectedKeys={[menuSelectedKey]}
            onSelect={({ key }) => setMenuSelectedKey(key)}
            onOpenChange={(keys) => {
              const latestKey = keys.find((key) => !openKeys.includes(key));
              if (latestKey && rootSubmenuKeys.includes(latestKey)) {
                setOpenKeys([latestKey]);
              } else {
                setOpenKeys(keys);
              }
            }}
          >
            {currentProgram === programEnum.ESPN ? (
              <EspnMenuSection
                espnAdminItems={espnAdminItems}
                espnFieldItems={espnFieldItems}
                counts={espnCounts}
                countsLoading={espnCountsLoading}
                roles={roles}
                role={espnRole}
              />
            ) : (
              <>
                <AdminMenuSections
                  showGlobalAdmin={roles?.includes(SUPER_ADMIN)}
                  showFacilityAdmin={roles?.includes(ADMIN)}
                  facilityType={authUser?.facility_type}
                  navStyle={navStyle}
                />

                {enlis_program === programEnum.GENERAL && !isFacilityAdmin && (
                  <GeneralProgramMenuSection
                    roles={roles}
                    role={role}
                    countUi={countUi}
                  />
                )}

                {isPublicProgram && (
                  <PublicProgramsMenuSection
                    roles={roles}
                    role={role}
                    isGlobalAdmin={isGlobalAdmin}
                    isFacilityAdmin={isFacilityAdmin}
                    canSeeFacilityAnalytics={canSeeFacilityAnalytics}
                    currentProgram={currentProgram}
                    flatPublicPrograms={flatPublicPrograms}
                    currentState={currentState}
                    currentLga={currentLga}
                    navStyle={navStyle}
                    countUi={countUi}
                  />
                )}

                <LocationAnalyticsMenuSection
                  userLocation={authUser?.user_location}
                  isSuperAdmin={roles.includes(SUPER_ADMIN)}
                />

                <UniversalMenuSections />
              </>
            )}
          </Menu>
        </CustomScrollbars>
      </div>
    </>
  );
};

SidebarContent.propTypes = {
  sidebarCollapsed: PropTypes.bool,
  setSidebarCollapsed: PropTypes.func,
};

// define memo for sidebar content
const memoSidebarContent = React.memo(SidebarContent);

export default memoSidebarContent;
